import React, { useState, useEffect } from 'react'
import { css } from '@emotion/core'
import { colors } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import AccessibleContent from '../../AccessibleContent'

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}
function ProgressDial({
  inactive = true,
  gap = 1,
  icon,
  value = 4,
  unit = 'Weeks',
  progress = 40,
  width = 330,
  showProgress = false,
}) {
  // progress is between 0 - 100
  // value is what is shown on the screen
  const [currentValue, setCurrentValue] = useState(value)
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const ringSize = width
  const strokeWidth = (width / 220) * 10 // Original width was 220px
  const adjustedRingSize = ringSize - strokeWidth * 2
  const gapSize = gap <= 0 ? 1 : gap
  const [t] = useTranslation()

  useEffect(() => {
    let start
    const duration = 1000 // Duration of the animation in milliseconds
    let currentAnimatedProgress = 0

    function animate(timestamp) {
      if (!start) start = timestamp
      const elapsed = timestamp - start
      const fraction = elapsed / duration
      currentAnimatedProgress = progress * easeInOutQuad(fraction)

      if (fraction < 1) {
        setAnimatedProgress(currentAnimatedProgress)
        requestAnimationFrame(animate)
      } else {
        setAnimatedProgress(progress)
      }
      if (currentAnimatedProgress && progress) {
        const percentComplete = currentAnimatedProgress / progress
        setCurrentValue((percentComplete * value).toFixed())
      }
    }

    requestAnimationFrame(animate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress])

  const radius = adjustedRingSize / 2
  const circumference = 2 * Math.PI * radius

  const offset = circumference - (animatedProgress / 100) * circumference
  // const offset = calculateOffset(currentProgress);
  const angleInDegrees = 270 + animatedProgress * 3.6
  const angleInRadians = angleInDegrees * (Math.PI / 180)

  const xOffset = radius * Math.cos(angleInRadians)
  const yOffset = radius * Math.sin(angleInRadians)

  const ProgressDialCss = css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${ringSize}px;
    height: ${ringSize}px;

    /* Flame icon */
    .flame-icon {
      position: absolute;
      top: calc(50% - ${yOffset}px);
      inset-inline-start: calc(50% - ${xOffset}px);
      transform: translate(-50%, -50%);
      img {
        width: ${(width / 220) * 100}px;
      }
    }

    .innerCircle {
      box-shadow: 0px 0px 80px 0px rgba(200, 87, 4, 1);
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${adjustedRingSize - (width / 220) * gapSize}px;
      height: ${adjustedRingSize - (width / 220) * gapSize}px;
      opacity: ${inactive ? '0.7' : '1'};
      background-color: ${inactive ? colors.gray00 : colors.yellow60};
      border-radius: 50%;
    }

    /* SVG styles */
    svg {
      position: absolute;
      transform: rotate(90deg);
      width: ${ringSize}px;
      height: ${ringSize}px;
    }

    circle {
      fill: none;
      stroke-width: ${strokeWidth}px;
      r: ${radius};
      cx: ${ringSize / 2};
      cy: ${ringSize / 2};
    }

    .progress-track {
      stroke: rgba(255, 255, 255, 0);
    }

    .progress-fill {
      stroke: #b14402;
      stroke-linecap: round;
    }

    .text {
      position: relative;
      top: ${(width / 220) * -10}px;
    }

    /* Weeks count text */
    .weeks-count {
      height: 100%;
      display: flex;
      font-size: ${(width / 220) * 82}px;
      font-weight: bold;
      color: #b14402;
      text-align: center;
    }

    /* Weeks text below the count */
    .weeks-text {
      top: ${((width / 220) * 100) / 2}px;
      text-align: center;
      font-size: ${(width / 220) * 16}px;
      ${inactive ? `color: ${colors.gray90};` : `color: ${colors.yellow10};`}
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: bold;
    }
  `

  return (
    <div css={ProgressDialCss}>
      {showProgress && (
        <svg>
          <circle className="progress-track" />
          <circle className="progress-fill" strokeDasharray={circumference} strokeDashoffset={offset} />
        </svg>
      )}
      <div className="innerCircle">
        {showProgress && (
          <div className="flame-icon">
            <img src={icon} alt={t('progress.flame', 'flame')} />
          </div>
        )}
        <div className="text">
          <div className="weeks-count">
            <img src={icon} alt="" />
          </div>
          {unit === 'Weeks' && (
            <AccessibleContent
              accessibleLabel={t(
                'streaks.you-have.a11y.label',
                '{count, plural, one {# Activity completed} other {# Activities completed}}',
                { count: currentValue }
              )}
            >
              <div className="weeks-text">{t('streaks.weeks', '{count} weeks', { count: currentValue })}</div>
            </AccessibleContent>
          )}
        </div>
      </div>
    </div>
  )
}
export default ProgressDial
